import React, { useState, useEffect } from 'react';
import { Card, Button, Descriptions, message, Divider, Input, Form, Table, Select, Progress, Modal, Tag, Pagination, Switch } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import api from '../api/axios';

const DeviceDetail = ({ device, onBack }) => {
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [otaUpdates, setOtaUpdates] = useState([]);
  const [deviceUpdateStatus, setDeviceUpdateStatus] = useState(null);
  const [deviceLogFiles, setDeviceLogFiles] = useState([]);
  const [deviceLogFilesTotal, setDeviceLogFilesTotal] = useState(0);
  const [otaModalVisible, setOtaModalVisible] = useState(false);
  const [selectedOtaId, setSelectedOtaId] = useState(null);
  // 指令日志
  const [commandLogs, setCommandLogs] = useState([]);
  const [commandLogsTotal, setCommandLogsTotal] = useState(0);
  // 日志文件查看
  const [logFileContentModalVisible, setLogFileContentModalVisible] = useState(false);
  const [logFileContent, setLogFileContent] = useState(null);
  const [logFileContentLoading, setLogFileContentLoading] = useState(false);
  // 学生数据同步相关状态
  const [syncStatus, setSyncStatus] = useState(null);
  const [deviceStudentStatus, setDeviceStudentStatus] = useState(null);
  const [queryCardNumber, setQueryCardNumber] = useState('');
  const [queryResult, setQueryResult] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncType, setSyncType] = useState('full');
  const [deviceStatusModalVisible, setDeviceStatusModalVisible] = useState(false);
  const [logFilePage, setLogFilePage] = useState(1);
  // 设备参数设置相关状态
  const [deviceSettings, setDeviceSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [newSettingModalVisible, setNewSettingModalVisible] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [newSettingForm] = Form.useForm();
  const [editingSettingForm] = Form.useForm();
  // 学生应用相关状态
  const [deviceApps, setDeviceApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);

  // 获取设备状态
  const fetchDeviceStatus = async () => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/status`);
      setDeviceStatus(response.data);
    } catch (error) {
      message.error('获取设备状态失败');
    }
  };

  useEffect(() => {
    fetchDeviceStatus();
    // 每5秒更新一次状态
    const interval = setInterval(fetchDeviceStatus, 5000);
    return () => clearInterval(interval);
  }, [device.id]);

  // 电源控制
  const handlePowerControl = async (action) => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/control/power`, { action });
      message.success(`设备${action === 'on' ? '开机' : action === 'off' ? '关机' : '重启'}命令发送成功`);
    } catch (error) {
      message.error('控制命令发送失败');
    } finally {
      setLoading(false);
    }
  };

  // 屏幕控制
  const handleScreenControl = async (action) => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/control/screen`, { action });
      message.success(`屏幕${action === 'on' ? '开启' : '关闭'}命令发送成功`);
    } catch (error) {
      message.error('控制命令发送失败');
    } finally {
      setLoading(false);
    }
  };

  // 应用管理
  const handleAppControl = async (values) => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/control/app`, values);
      message.success(`应用${values.action === 'start' ? '启动' : '停止'}命令发送成功`);
      form.resetFields();
    } catch (error) {
      message.error('控制命令发送失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取OTA更新列表
  const fetchOtaUpdates = async () => {
    try {
      const response = await api.get('/admin/devices/ota/updates', {
        params: { deviceType: device.type }
      });
      setOtaUpdates(response.data);
    } catch (error) {
      message.error('获取OTA更新列表失败');
    }
  };

  // 获取设备更新状态
  const fetchDeviceUpdateStatus = async () => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/ota/status`);
      setDeviceUpdateStatus(response.data);
    } catch (error) {
      message.error('获取设备更新状态失败');
    }
  };

  // 获取设备参数设置
  const fetchDeviceSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await api.get(`/admin/devices/${device.id}/settings`);
      setDeviceSettings(response.data);
    } catch (error) {
      message.error('获取设备参数设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 添加设备参数设置
  const handleAddSetting = async (values) => {
    setSettingsLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/settings`, values);
      message.success('添加设备参数设置成功');
      setNewSettingModalVisible(false);
      newSettingForm.resetFields();
      fetchDeviceSettings();
    } catch (error) {
      message.error('添加设备参数设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 更新设备参数设置
  const handleUpdateSetting = async (values) => {
    setSettingsLoading(true);
    try {
      await api.put(`/admin/devices/${device.id}/settings/${editingSetting.id}`, values);
      message.success('更新设备参数设置成功');
      setEditingSetting(null);
      editingSettingForm.resetFields();
      fetchDeviceSettings();
    } catch (error) {
      message.error('更新设备参数设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 删除设备参数设置
  const handleDeleteSetting = async (settingId) => {
    if (!window.confirm('确定要删除这个设置吗？')) {
      return;
    }
    setSettingsLoading(true);
    try {
      await api.delete(`/admin/devices/${device.id}/settings/${settingId}`);
      message.success('删除设备参数设置成功');
      fetchDeviceSettings();
    } catch (error) {
      message.error('删除设备参数设置失败');
    } finally {
      setSettingsLoading(false);
    }
  };

  // 发送更新配置指令
  const handleApplySettings = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/settings/apply`);
      message.success('配置更新指令已发送');
      fetchCommandLogs(1);
    } catch (error) {
      message.error('发送配置更新指令失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取设备学生应用
  const fetchDeviceApps = async () => {
    setAppsLoading(true);
    try {
      const response = await api.get(`/admin/devices/${device.id}/student-apps`);
      setDeviceApps(response.data);
    } catch (error) {
      message.error('获取设备学生应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 更新设备学生应用
  const handleUpdateDeviceApps = async () => {
    setAppsLoading(true);
    try {
      const enabledAppIds = deviceApps.filter(app => app.is_enabled).map(app => app.id);
      await api.put(`/admin/devices/${device.id}/student-apps`, { app_ids: enabledAppIds });
      message.success('更新设备学生应用成功');
    } catch (error) {
      message.error('更新设备学生应用失败');
    } finally {
      setAppsLoading(false);
    }
  };

  // 切换应用启用状态
  const toggleAppEnabled = (appId) => {
    setDeviceApps(deviceApps.map(app => {
      if (app.id === appId) {
        return { ...app, is_enabled: !app.is_enabled };
      }
      return app;
    }));
  };

  // 获取设备日志文件列表（分页）
  const fetchDeviceLogFiles = async (page = 1) => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/logs`, {
        params: { page, size: 10 }
      });
      setDeviceLogFiles(response.data.data || []);
      setDeviceLogFilesTotal(response.data.total || 0);
    } catch (error) {
      console.error('获取设备日志文件列表失败:', error);
    }
  };

  // 获取指令日志（分页）
  const fetchCommandLogs = async (page = 1, pageSize = 5) => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/commands`, {
        params: { page, size: pageSize }
      });
      setCommandLogs(response.data.data || []);
      setCommandLogsTotal(response.data.total || 0);
    } catch (error) {
      console.error('获取指令日志失败:', error);
    }
  };

  // 触发设备上报学生数据
  const handleTriggerStudentReport = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/control/student-report`);
      message.success('上报学生数据指令已发送');
      fetchCommandLogs(1);
    } catch (error) {
      message.error('发送指令失败');
    } finally {
      setLoading(false);
    }
  };

  // 触发设备上报日志
  const handleTriggerLogReport = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/control/log-report`, { logType: 'all' });
      message.success('上报日志指令已发送');
      fetchCommandLogs(1);
    } catch (error) {
      message.error('发送指令失败');
    } finally {
      setLoading(false);
    }
  };

  // 查看日志文件内容
  const handleViewLogFileContent = async (logFile) => {
    setLogFileContentLoading(true);
    setLogFileContentModalVisible(true);
    try {
      const response = await api.get(`/admin/devices/${device.id}/logs/${logFile.id}/content`);
      setLogFileContent({
        fileName: logFile.file_name,
        content: response.data.content
      });
    } catch (error) {
      message.error('读取日志文件失败');
      setLogFileContentModalVisible(false);
    } finally {
      setLogFileContentLoading(false);
    }
  };

  // 下载日志文件
  const handleDownloadLogFile = async (logFile) => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/logs/${logFile.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', logFile.file_name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('日志文件下载成功');
    } catch (error) {
      message.error('下载日志文件失败');
    }
  };

  // 触发设备更新
  const handleOtaUpdate = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/ota/update`, { otaId: selectedOtaId });
      message.success('更新指令已提交');
      setOtaModalVisible(false);
      // 刷新更新状态
      fetchDeviceUpdateStatus();
    } catch (error) {
      message.error('触发更新失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取同步状态
  const fetchSyncStatus = async (page = 1, pageSize = 5) => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/sync/status`, {
        params: { page, size: pageSize }
      });
      setSyncStatus(response.data);
    } catch (error) {
      message.error('获取同步状态失败');
    }
  };

  // 获取设备学生数据状态（从设备端上报的数据）
  const fetchDeviceStudentStatus = async () => {
    try {
      const response = await api.get(`/admin/devices/${device.id}/students/device-status`);
      setDeviceStudentStatus(response.data);
      setDeviceStatusModalVisible(true);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        message.warning('设备未上报学生数据状态');
      } else {
        message.error('获取设备学生数据状态失败');
      }
    }
  };

  // 触发学生数据同步
  const handleSyncStudents = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/devices/${device.id}/sync/students`, { sync_type: syncType, force: true });
      message.success('同步任务已创建');
      setSyncModalVisible(false);
      // 刷新同步状态
      fetchSyncStatus();
    } catch (error) {
      message.error('触发同步失败');
    } finally {
      setLoading(false);
    }
  };

  // 查询学生信息
  const handleQueryStudent = async () => {
    if (!queryCardNumber) {
      message.warning('请输入学生卡号');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post(`/admin/devices/${device.id}/students/query`, { card_number: queryCardNumber });
      setQueryResult(response.data);
    } catch (error) {
      message.error('查询学生信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 校验数据一致性（使用设备上报的数据）
  const handleVerifyData = async () => {
    setLoading(true);
    try {
      // 调用新的compare接口，直接获取设备上报数据与服务器数据进行对比
      const response = await api.get(`/admin/devices/${device.id}/students/compare`);
      setVerifyResult(response.data);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        message.warning('设备未上报学生数据，请等待设备上报后再进行校验');
      } else {
        message.error('校验数据一致性失败');
      }
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchOtaUpdates();
    fetchDeviceUpdateStatus();
    fetchDeviceLogFiles();
    fetchCommandLogs(1);
    fetchSyncStatus();
    fetchDeviceSettings();
    fetchDeviceApps();
  }, [device.id]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>设备详情</h2>
        <Button onClick={onBack}>返回列表</Button>
      </div>

      <Card title="设备基本信息" style={{ marginBottom: 16 }}>
        <Descriptions bordered>
          <Descriptions.Item label="设备名称">{device.name}</Descriptions.Item>
          <Descriptions.Item label="设备ID">{device.device_id}</Descriptions.Item>
          <Descriptions.Item label="设备类型">{device.type}</Descriptions.Item>
          <Descriptions.Item label="学校">{device.school_name}</Descriptions.Item>
            <Descriptions.Item label="班级">{device.class_name}</Descriptions.Item>
            <Descriptions.Item label="学校ID">{device.school_id}</Descriptions.Item>
            <Descriptions.Item label="班级ID">{device.class_id}</Descriptions.Item>
          <Descriptions.Item label="设备状态">
            <span className={device.status === '在线' ? 'status-online' : 'status-offline'}>
              {device.status}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="电池电量">
            <span className={
              device.battery > 70 ? 'battery-high' : 
              device.battery > 30 ? 'battery-medium' : 'battery-low'
            }>
              {device.battery}%
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="IP地址">{device.ip_address}</Descriptions.Item>
          <Descriptions.Item label="最后心跳" span={2}>
            {new Date(device.last_heartbeat).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="设备控制" style={{ marginBottom: 16 }}>
        <h3>电源控制</h3>
        <div className="control-buttons">
          <Button type="primary" onClick={() => handlePowerControl('on')} loading={loading}>
            开机
          </Button>
          <Button danger onClick={() => handlePowerControl('off')} loading={loading}>
            关机
          </Button>
          <Button onClick={() => handlePowerControl('restart')} loading={loading}>
            重启
          </Button>
        </div>

        <Divider />

        <h3>屏幕控制</h3>
        <div className="control-buttons">
          <Button type="primary" onClick={() => handleScreenControl('on')} loading={loading}>
            亮屏
          </Button>
          <Button onClick={() => handleScreenControl('off')} loading={loading}>
            息屏
          </Button>
        </div>

        <Divider />

        <h3>应用管理</h3>
        <Form
          form={form}
          layout="inline"
          onFinish={handleAppControl}
        >
          <Form.Item
            name="appName"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input placeholder="请输入应用包名" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item
            name="action"
            initialValue="start"
          >
            <select style={{ width: 100, padding: '4px 8px', marginRight: 8 }}>
              <option value="start">启动</option>
              <option value="stop">停止</option>
            </select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              执行
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <h3>数据与日志上报</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          <Button type="primary" onClick={handleTriggerStudentReport} loading={loading}>
            触发设备上报学生数据
          </Button>
          <Button onClick={handleTriggerLogReport} loading={loading}>
            上报日志指令
          </Button>
        </div>

      </Card>

      <Card title="设备参数设置" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <Button type="primary" onClick={() => setNewSettingModalVisible(true)} loading={settingsLoading}>
              添加设置
            </Button>
            <Button onClick={handleApplySettings} loading={loading} style={{ marginLeft: 8 }}>
              发送更新配置指令
            </Button>
          </div>
        </div>

        <Table 
          columns={[
            { title: '设置键', dataIndex: 'setting_key', key: 'setting_key', width: 150 },
            { title: '设置值', dataIndex: 'setting_value', key: 'setting_value' },
            { title: '描述', dataIndex: 'description', key: 'description' },
            { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: time => new Date(time).toLocaleString() },
            { title: '操作', key: 'action', render: (text, record) => (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button 
                  size="small" 
                  onClick={() => { 
                    setEditingSetting(record); 
                    editingSettingForm.setFieldsValue({ setting_value: record.setting_value, description: record.description });
                  }}
                >
                  编辑
                </Button>
                <Button 
                  size="small" 
                  danger 
                  onClick={() => handleDeleteSetting(record.id)}
                >
                  删除
                </Button>
              </div>
            )}
          ]} 
          dataSource={deviceSettings} 
          rowKey="id" 
          loading={settingsLoading}
          pagination={false}
          bordered
        />

        {/* 添加设置模态框 */}
        <Modal
          title="添加设备参数设置"
          open={newSettingModalVisible}
          onOk={() => newSettingForm.submit()}
          onCancel={() => { setNewSettingModalVisible(false); newSettingForm.resetFields(); }}
          okText="确定"
          cancelText="取消"
          confirmLoading={settingsLoading}
        >
          <Form form={newSettingForm} layout="vertical" onFinish={handleAddSetting}>
            <Form.Item
              label="设置键"
              name="setting_key"
              rules={[
                { required: true, message: '请输入设置键' },
                { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: '设置键只能以字母开头，包含字母、数字和下划线' }
              ]}
            >
              <Input placeholder="如: openFace, bdKey" />
            </Form.Item>
            <Form.Item
              label="设置值"
              name="setting_value"
              rules={[{ required: true, message: '请输入设置值' }]}
            >
              <Input placeholder="设置值" />
            </Form.Item>
            <Form.Item
              label="描述"
              name="description"
            >
              <Input.TextArea placeholder="设置描述（可选）" rows={3} />
            </Form.Item>
          </Form>
        </Modal>

        {/* 编辑设置模态框 */}
        {editingSetting && (
          <Modal
            title="编辑设备参数设置"
            open={!!editingSetting}
            onOk={() => editingSettingForm.submit()}
            onCancel={() => { setEditingSetting(null); editingSettingForm.resetFields(); }}
            okText="确定"
            cancelText="取消"
            confirmLoading={settingsLoading}
          >
            <Form form={editingSettingForm} layout="vertical" onFinish={handleUpdateSetting}>
              <Form.Item label="设置键">
                <Input disabled value={editingSetting.setting_key} />
              </Form.Item>
              <Form.Item
                label="设置值"
                name="setting_value"
                rules={[{ required: true, message: '请输入设置值' }]}
              >
                <Input placeholder="设置值" />
              </Form.Item>
              <Form.Item
                label="描述"
                name="description"
              >
                <Input.TextArea placeholder="设置描述（可选）" rows={3} />
              </Form.Item>
            </Form>
          </Modal>
        )}
      </Card>

      <Card title="学生应用配置" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <span>勾选需要启用的应用</span>
          </div>
          <Button type="primary" onClick={handleUpdateDeviceApps} loading={appsLoading}>
            保存配置
          </Button>
        </div>

        {appsLoading ? (
          <p>加载中...</p>
        ) : (
          <div>
            <Divider orientation="left">主题应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
              {deviceApps.filter(app => app.app_type === 'theme').map(app => (
                <div 
                  key={app.id} 
                  style={{ 
                    padding: 16, 
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8, 
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch 
                      checked={app.is_enabled} 
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>

            <Divider orientation="left">分类应用</Divider>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {deviceApps.filter(app => app.app_type === 'category').map(app => (
                <div 
                  key={app.id} 
                  style={{ 
                    padding: 16, 
                    border: app.is_enabled ? '2px solid #1890ff' : '2px solid #d9d9d9',
                    borderRadius: 8, 
                    width: 150,
                    backgroundColor: app.is_enabled ? '#e6f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleAppEnabled(app.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontWeight: 'bold' }}>{app.app_name}</span>
                    <Switch 
                      checked={app.is_enabled} 
                      onChange={(checked, e) => {
                        e.stopPropagation();
                        toggleAppEnabled(app.id);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {app.description || '暂无描述'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card 
        title="指令日志"
        extra={
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={() => fetchCommandLogs(1)}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        <Table 
          columns={[
            { title: '指令ID', dataIndex: 'id', key: 'id', width: 80 },
            { title: '指令类型', dataIndex: 'command', key: 'command', render: cmd => (
              <Tag color={
                cmd === 'power' ? 'blue' : 
                cmd === 'screen' ? 'cyan' : 
                cmd === 'app' ? 'purple' :
                cmd === 'student' ? 'green' :
                cmd === 'log' ? 'orange' : 'default'
              }>
                {cmd === 'power' ? '电源' : 
                 cmd === 'screen' ? '屏幕' : 
                 cmd === 'app' ? '应用' : 
                 cmd === 'student' ? '学生数据' :
                 cmd === 'log' ? '日志' : cmd}
              </Tag>
            )},
            { title: '操作', dataIndex: 'action', key: 'action' },
            { title: '状态', dataIndex: 'status', key: 'status', render: status => (
              <Tag color={
                status === 'pending' ? 'orange' : 
                status === 'executing' ? 'processing' : 
                status === 'completed' ? 'success' : 
                status === 'failed' ? 'error' : 'default'
              }>
                {status === 'pending' ? '待执行' : 
                 status === 'executing' ? '执行中' : 
                 status === 'completed' ? '已完成' : 
                 status === 'failed' ? '失败' : status}
              </Tag>
            )},
            { title: '下发时间', dataIndex: 'created_at', key: 'created_at', render: time => new Date(time).toLocaleString() },
            { title: '执行时间', dataIndex: 'executed_at', key: 'executed_at', render: time => time ? new Date(time).toLocaleString() : '-' }
          ]} 
          dataSource={commandLogs} 
          rowKey="id" 
          pagination={{ 
            pageSize: 5, 
            total: commandLogsTotal,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20', '50'],
            showQuickJumper: true,
            onChange: (page, pageSize) => fetchCommandLogs(page, pageSize),
            onShowSizeChange: (current, pageSize) => fetchCommandLogs(1, pageSize)
          }}
          loading={loading}
        />
      </Card>

      <Card title="设备状态历史">
        <p>状态历史记录将在此显示</p>
      </Card>

      <Card title="OTA更新" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>更新管理</h3>
          <Button type="primary" onClick={() => setOtaModalVisible(true)}>
            触发更新
          </Button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h4>当前更新状态</h4>
          {deviceUpdateStatus ? (
            deviceUpdateStatus.status === 'no_update' ? (
              <p>暂无更新任务</p>
            ) : (
              <div>
                <p>状态: {deviceUpdateStatus.status}</p>
                <p>进度: {deviceUpdateStatus.progress}%</p>
                <Progress percent={deviceUpdateStatus.progress} status={deviceUpdateStatus.status === 'completed' ? 'success' : deviceUpdateStatus.status === 'failed' ? 'exception' : 'active'} />
                {deviceUpdateStatus.error_message && (
                  <p style={{ color: 'red' }}>错误信息: {deviceUpdateStatus.error_message}</p>
                )}
              </div>
            )
          ) : (
            <p>加载中...</p>
          )}
        </div>

        <div>
          <h4>可用更新</h4>
          <Table 
            columns={[
              { title: '版本号', dataIndex: 'version', key: 'version' },
              { title: '描述', dataIndex: 'description', key: 'description' },
              { title: '文件大小', dataIndex: 'file_size', key: 'file_size', render: size => `${(size / 1024 / 1024).toFixed(2)} MB` },
              { title: '状态', dataIndex: 'status', key: 'status' },
              { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: time => new Date(time).toLocaleString() }
            ]} 
            dataSource={otaUpdates} 
            rowKey="id" 
            pagination={{ pageSize: 5 }}
          />
        </div>
      </Card>

      <Card title="设备日志文件">
        <div style={{ marginBottom: 16 }}>
          <Button onClick={() => fetchDeviceLogFiles(logFilePage)} loading={loading}>
            刷新日志文件列表
          </Button>
        </div>
        <Table 
          columns={[
            { title: '文件名', dataIndex: 'file_name', key: 'file_name' },
            { title: '日志类型', dataIndex: 'log_type', key: 'log_type', render: type => (
              <Tag color={type === 'error' ? 'red' : type === 'system' ? 'blue' : 'green'}>
                {type === 'error' ? '错误' : type === 'system' ? '系统' : '全部'}
              </Tag>
            )},
            { title: '文件大小', dataIndex: 'file_size', key: 'file_size', render: size => size ? `${(size / 1024).toFixed(2)} KB` : '-' },
            { title: '上传时间', dataIndex: 'created_at', key: 'created_at', render: time => new Date(time).toLocaleString() },
            { title: '操作', key: 'actions', render: (_, logFile) => (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="link" onClick={() => handleViewLogFileContent(logFile)}>
                  查看内容
                </Button>
                <Button type="link" onClick={() => handleDownloadLogFile(logFile)}>
                  下载
                </Button>
              </div>
            )}
          ]} 
          dataSource={deviceLogFiles} 
          rowKey="id" 
          pagination={false}
        />
        {deviceLogFilesTotal > 0 && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              current={logFilePage}
              total={deviceLogFilesTotal}
              pageSize={10}
              onChange={(page) => { setLogFilePage(page); fetchDeviceLogFiles(page); }}
              showQuickJumper
            />
          </div>
        )}
      </Card>

      <Card title="学生数据同步" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>数据同步管理</h3>
          <Button type="primary" onClick={() => setSyncModalVisible(true)}>
            触发同步
          </Button>
        </div>

        {/* 同步状态概览 */}
        <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          {syncStatus ? (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <span style={{ fontWeight: 'bold' }}>同步状态: </span>
                  <Tag color={syncStatus.is_synced ? 'green' : 'red'}>
                    {syncStatus.is_synced ? '数据一致' : '数据不一致'}
                  </Tag>
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>最后同步: </span>
                  {syncStatus.last_sync_time ? new Date(syncStatus.last_sync_time).toLocaleString() : '从未同步'}
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>服务器学生数: </span>
                  {syncStatus.server_student_count} 人
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>设备学生数: </span>
                  {syncStatus.device_student_count} 人
                </div>
              </div>
              {!syncStatus.is_synced && (
                <p style={{ color: '#ff4d4f', marginTop: 12 }}>
                  数据不一致，建议执行同步操作
                </p>
              )}
            </div>
          ) : (
            <p>加载中...</p>
          )}
        </div>

        <Divider />

        {/* 快捷操作 */}
        <div style={{ marginBottom: 16 }}>
          <h4>快捷操作</h4>
          <div style={{ display: 'flex', gap: 16 }}>
            <Button onClick={fetchSyncStatus} loading={loading}>
              刷新状态
            </Button>
            <Button onClick={fetchDeviceStudentStatus} loading={loading}>
              查看设备数据
            </Button>
            <Button onClick={handleVerifyData} loading={loading} type="primary">
              数据校验
            </Button>
            <Button onClick={() => setSyncModalVisible(true)} loading={loading}>
              全量同步
            </Button>
          </div>
        </div>

        {/* 学生查询 */}
        <Divider />
        <div style={{ marginBottom: 16 }}>
          <h4>学生查询（诊断用）</h4>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input 
              placeholder="请输入学生卡号" 
              value={queryCardNumber}
              onChange={(e) => setQueryCardNumber(e.target.value)}
              style={{ width: 200 }}
              onPressEnter={handleQueryStudent}
            />
            <Button type="primary" onClick={handleQueryStudent} loading={loading}>
              查询
            </Button>
          </div>
          {queryResult && (
            <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              {queryResult.found ? (
                <div>
                  <p>查询结果：找到学生</p>
                  <p>姓名: {queryResult.student.name}</p>
                  <p>班级: {queryResult.student.class_name}</p>
                  <p>卡号: {queryResult.student.card_id}</p>
                </div>
              ) : (
                <p style={{ color: '#ff4d4f' }}>未找到该学生信息</p>
              )}
            </div>
          )}
        </div>

        {/* 数据校验结果 */}
        <Divider />
        <div>
          <h4>数据一致性校验结果</h4>
          {verifyResult ? (
            <div style={{ padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
                <div>
                  <span style={{ fontWeight: 'bold' }}>校验状态: </span>
                  <Tag color={verifyResult.is_consistent ? 'green' : 'red'}>
                    {verifyResult.is_consistent ? '一致' : '不一致'}
                  </Tag>
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>服务器: </span>
                  {verifyResult.server_count} 人
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>设备: </span>
                  {verifyResult.device_count} 人
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>有效数据: </span>
                  {verifyResult.valid_count || 0} 条
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>无效数据: </span>
                  <span style={{ color: verifyResult.invalid_count > 0 ? '#ff4d4f' : 'inherit' }}>
                    {verifyResult.invalid_count || 0} 条
                  </span>
                </div>
              </div>
              <p style={{ marginBottom: 8 }}><strong>建议:</strong> {verifyResult.suggestion}</p>
              {verifyResult.invalid_cards && verifyResult.invalid_cards.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ marginBottom: 4, color: '#ff4d4f' }}><strong>设备无效卡号:</strong> {verifyResult.invalid_cards.length} 个</p>
                  <p style={{ fontSize: 12, color: '#8c8c8c' }}>{verifyResult.invalid_cards.join(', ')}</p>
                </div>
              )}
              {verifyResult.missing_on_device && verifyResult.missing_on_device.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ marginBottom: 4, color: '#ff4d4f' }}><strong>设备缺失卡号:</strong> {verifyResult.missing_on_device.length} 个</p>
                  <p style={{ fontSize: 12, color: '#8c8c8c' }}>{verifyResult.missing_on_device.slice(0, 10).join(', ')}{verifyResult.missing_on_device.length > 10 ? '...' : ''}</p>
                </div>
              )}
              {verifyResult.extra_on_device && verifyResult.extra_on_device.length > 0 && (
                <div>
                  <p style={{ marginBottom: 4, color: '#faad14' }}><strong>设备多余卡号:</strong> {verifyResult.extra_on_device.length} 个</p>
                  <p style={{ fontSize: 12, color: '#8c8c8c' }}>{verifyResult.extra_on_device.slice(0, 10).join(', ')}{verifyResult.extra_on_device.length > 10 ? '...' : ''}</p>
                </div>
              )}
            </div>
          ) : (
            <p>点击"数据校验"按钮进行数据一致性校验</p>
          )}
        </div>

        <Divider />

        {/* 同步历史 */}
        <div>
          <h4>同步历史记录</h4>
          {syncStatus && syncStatus.sync_history && syncStatus.sync_history.length > 0 ? (
            <Table 
              columns={[
                { title: '同步时间', dataIndex: 'sync_time', key: 'sync_time', render: time => new Date(time).toLocaleString() },
                { title: '同步类型', dataIndex: 'sync_type', key: 'sync_type' },
                { title: '状态', dataIndex: 'status', key: 'status', render: status => (
                  <Tag color={status === 'synced' ? 'green' : status === 'failed' ? 'red' : 'orange'}>
                    {status === 'synced' ? '成功' : status === 'failed' ? '失败' : '进行中'}
                  </Tag>
                )},
                { title: '同步条数', dataIndex: 'count', key: 'count' },
                { title: '消息', dataIndex: 'message', key: 'message' }
              ]} 
              dataSource={syncStatus.sync_history} 
              rowKey="id" 
              pagination={{ 
                pageSize: 5, 
                total: syncStatus.sync_history_total || 0,
                showSizeChanger: true,
                pageSizeOptions: ['5', '10', '20', '50'],
                showQuickJumper: true,
                onChange: (page, pageSize) => fetchSyncStatus(page, pageSize),
                onShowSizeChange: (current, pageSize) => fetchSyncStatus(1, pageSize)
              }}
            />
          ) : (
            <p>暂无同步记录</p>
          )}
        </div>
      </Card>

      {/* 同步触发模态框 */}
      <Modal
        title="触发学生数据同步"
        open={syncModalVisible}
        onOk={handleSyncStudents}
        onCancel={() => setSyncModalVisible(false)}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item
            label="同步类型"
            name="syncType"
            rules={[{ required: true, message: '请选择同步类型' }]}
          >
            <Select
              placeholder="请选择同步类型"
              value={syncType}
              onChange={value => setSyncType(value)}
              style={{ width: '100%' }}
            >
              <Select.Option value="full">全量同步</Select.Option>
              <Select.Option value="incremental">增量同步</Select.Option>
            </Select>
          </Form.Item>
          <p style={{ color: '#8c8c8c', fontSize: '12px' }}>
            全量同步：覆盖设备上所有学生数据
            <br />
            增量同步：仅同步新增或更新的数据
          </p>
        </Form>
      </Modal>

      {/* OTA更新模态框 */}
      <Modal
        title="触发OTA更新"
        open={otaModalVisible}
        onOk={handleOtaUpdate}
        onCancel={() => setOtaModalVisible(false)}
        okText="确定"
        cancelText="取消"
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item
            label="选择更新版本"
            name="otaId"
            rules={[{ required: true, message: '请选择更新版本' }]}
          >
            <Select
              placeholder="请选择更新版本"
              value={selectedOtaId}
              onChange={value => setSelectedOtaId(value)}
              style={{ width: '100%' }}
            >
              {otaUpdates.map(update => (
                <Select.Option key={update.id} value={update.id}>
                  {update.version} - {update.description}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 设备学生数据状态模态框 */}
      <Modal
        title="设备学生数据详情"
        open={deviceStatusModalVisible}
        onCancel={() => setDeviceStatusModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDeviceStatusModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {deviceStudentStatus ? (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="设备ID">{deviceStudentStatus.device_id}</Descriptions.Item>
              <Descriptions.Item label="学生总数">{deviceStudentStatus.total_count} 人</Descriptions.Item>
              <Descriptions.Item label="有效数据">{deviceStudentStatus.valid_count} 条</Descriptions.Item>
              <Descriptions.Item label="无效数据">
                <span style={{ color: deviceStudentStatus.invalid_count > 0 ? '#ff4d4f' : 'inherit' }}>
                  {deviceStudentStatus.invalid_count} 条
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="存储状态">
                <Tag color={deviceStudentStatus.storage_status === 'healthy' ? 'green' : 'red'}>
                  {deviceStudentStatus.storage_status === 'healthy' ? '正常' : '异常'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="数据版本">{deviceStudentStatus.data_version || '未知'}</Descriptions.Item>
              <Descriptions.Item label="最后同步时间" span={2}>
                {deviceStudentStatus.last_sync_time ? new Date(deviceStudentStatus.last_sync_time).toLocaleString() : '从未同步'}
              </Descriptions.Item>
              <Descriptions.Item label="最后更新时间" span={2}>
                {deviceStudentStatus.last_updated ? new Date(deviceStudentStatus.last_updated).toLocaleString() : '未知'}
              </Descriptions.Item>
            </Descriptions>

            {deviceStudentStatus.invalid_cards && deviceStudentStatus.invalid_cards.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ color: '#ff4d4f' }}>无效卡号列表 ({deviceStudentStatus.invalid_cards.length} 个)</h4>
                <div style={{ maxHeight: 150, overflow: 'auto', padding: 8, backgroundColor: '#fff2f0', borderRadius: 4 }}>
                  {deviceStudentStatus.invalid_cards.join(', ')}
                </div>
              </div>
            )}

            {deviceStudentStatus.card_list && deviceStudentStatus.card_list.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4>设备卡号列表 ({deviceStudentStatus.card_list.length} 个，显示前100个)</h4>
                <div style={{ maxHeight: 200, overflow: 'auto', padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, fontSize: 12 }}>
                  {deviceStudentStatus.card_list.slice(0, 100).join(', ')}
                  {deviceStudentStatus.card_list.length > 100 && '...'}
                </div>
              </div>
            )}
          </div>
        ) : (
          <p>加载中...</p>
        )}
      </Modal>

      {/* 日志文件内容查看模态框 */}
      <Modal
        title={logFileContent ? `日志文件: ${logFileContent.fileName}` : '日志文件内容'}
        open={logFileContentModalVisible}
        onCancel={() => { setLogFileContentModalVisible(false); setLogFileContent(null); }}
        footer={[
          <Button key="close" onClick={() => { setLogFileContentModalVisible(false); setLogFileContent(null); }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {logFileContentLoading ? (
          <p>加载中...</p>
        ) : logFileContent ? (
          <div>
            <div style={{ marginBottom: 8, color: '#666', fontSize: 12 }}>
              文件名: {logFileContent.fileName}
            </div>
            <pre style={{ 
              maxHeight: 500, 
              overflow: 'auto', 
              padding: 16, 
              backgroundColor: '#1e1e1e', 
              color: '#d4d4d4', 
              borderRadius: 4,
              fontSize: 12,
              fontFamily: 'Consolas, Monaco, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all'
            }}>
              {logFileContent.content}
            </pre>
          </div>
        ) : (
          <p>暂无内容</p>
        )}
      </Modal>
    </div>
  );
};

export default DeviceDetail;
